import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram } from "lucide-react";
import Logo from "./Logo";
import { EASE } from "./Reveal";
import { CONTACT } from "../data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/process", label: "Process" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-md border-b border-bronze/10"
            : "bg-ivory/40 backdrop-blur-sm"
        }`}
      >
        <div
          className={`hidden md:block overflow-hidden transition-all duration-500 bg-espresso text-ivory/85 ${
            scrolled ? "h-0 opacity-0" : "h-10 opacity-100"
          }`}
        >
          <div className="container-lux h-10 flex items-center justify-between text-[12px]">
            <div className="flex items-center gap-5">
              <a
                href={`tel:+${CONTACT.whatsapp}`}
                data-testid="utility-phone-link"
                className="flex items-center gap-2 tracking-wide hover:text-gold transition-colors duration-300"
              >
                <Phone size={13} strokeWidth={1.5} /> {CONTACT.phoneDisplay}
              </a>
              <span className="h-3 w-px bg-ivory/20" aria-hidden="true" />
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="utility-email-link"
                className="flex items-center gap-2 tracking-wide hover:text-gold transition-colors duration-300"
              >
                <Mail size={13} strokeWidth={1.5} /> {CONTACT.email}
              </a>
              <span className="h-3 w-px bg-ivory/20" aria-hidden="true" />
              <span
                data-testid="utility-address"
                className="flex items-center gap-2 tracking-wide text-ivory/60"
              >
                <MapPin size={13} strokeWidth={1.5} /> {CONTACT.address}
              </span>
            </div>
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="utility-instagram-link"
              aria-label="Aurea Living on Instagram"
              className="grid place-items-center h-6 w-6 rounded-full border border-ivory/25 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <Instagram size={12} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div className="container-lux flex items-center justify-between py-3.5">
          <Link to="/" data-testid="nav-logo-link" aria-label="Aurea Living home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-line text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 ${
                    isActive ? "text-bronze" : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="rounded-full bg-bronze text-ivory px-7 py-2.5 text-[12px] font-medium tracking-[0.14em] uppercase transition-all duration-500 hover:bg-bronze-deep hover:-translate-y-0.5"
            >
              Book a Consultation
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="lg:hidden flex flex-col justify-center items-end gap-[7px] h-10 w-10"
          >
            <span
              className={`h-[1.5px] bg-ink transition-all duration-400 ${
                open ? "w-7 rotate-45 translate-y-[4.5px]" : "w-7"
              }`}
            />
            <span
              className={`h-[1.5px] bg-ink transition-all duration-400 ${
                open ? "w-7 -rotate-45 -translate-y-[4.5px]" : "w-5"
              }`}
            />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-40 bg-espresso text-ivory lg:hidden"
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <div className="h-full flex flex-col justify-center px-8 pt-20">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block font-serif text-4xl py-2.5 border-b border-ivory/10 ${
                        isActive ? "text-gold italic" : "text-ivory"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-8 text-[11px] font-mono uppercase tracking-[0.25em] text-ivory/50"
              >
                Jubilee Hills · Hyderabad
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
